import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { getPerformanceTrackingStats, formatApiError, getTenantSubdomain } from '../services/api';
import { getDateLocale } from '../i18n';
import { RS_CHART_SERIES, RS_CHART_GLOBAL_LINES } from '../utils/rsChartPalette';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const GlobalPerformanceTrackingPage = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    period: 'Last 30 Days',
    centre: '',
    team: '',
  });
  
  // Debounce timer
  const [debounceTimer, setDebounceTimer] = useState(null);
  
  // Fetch performance data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPerformanceTrackingStats(filters);
      setData(result);
    } catch (err) {
      const formattedError = formatApiError(err);
      setError(formattedError.generalError || t('performance.loadFailed'));
      console.error('Error fetching performance data:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  // Debounced filter change handler
  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    const timer = setTimeout(() => {
      fetchData();
    }, 500); // 500ms debounce
    
    setDebounceTimer(timer);
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [filters, fetchData]);
  
  // Initial fetch
  useEffect(() => {
    fetchData();
  }, []); // Only on mount
  
  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
    }));
  };
  
  
  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-red-500">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
          >
            {t('common:retry')}
          </button>
        </div>
      </div>
    );
  }
  
  if (loading && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <span className="material-symbols-outlined text-5xl text-primary animate-pulse">progress_activity</span>
        <p className="text-text-secondary">{t('performance.loadingData')}</p>
      </div>
    );
  }
  
  if (!data) {
    return null;
  }
  
  const { summary, badge_distribution, production_trends, top_performers, badge_by_team, filter_options } = data;
  const isRsTenant = (getTenantSubdomain() || '').toLowerCase() === 'rs';
  
  // Calculate badge distribution chart data
  const getBadgeChartData = () => {
    if (!badge_distribution || badge_distribution.length === 0) {
      return { segments: [], total: 0 };
    }
    
    const total = badge_distribution.reduce((sum, item) => sum + item.count, 0);
    let currentPercent = 0;
    const colors = isRsTenant
      ? RS_CHART_SERIES
      : ['#1162d4', '#60a5fa', '#93c5fd', '#e2e8f0', '#fbbf24', '#f59e0b', '#ef4444', '#10b981'];
    
    const segments = badge_distribution.map((item, index) => {
      const start = currentPercent;
      const end = currentPercent + (item.count / total * 100);
      currentPercent = end;
      
      return {
        ...item,
        startPercent: start,
        endPercent: end,
        color: colors[index % colors.length],
        percentage: ((item.count / total) * 100).toFixed(1)
      };
    });
    
    return { segments, total };
  };
  
  const badgeChartData = getBadgeChartData();
  
  // Helper function to get date range based on period
  const getDateRangeForPeriod = (period) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dates = [];
    
    if (period === 'Today') {
      dates.push(new Date(today));
    } else if (period === 'Yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      dates.push(yesterday);
    } else if (period === 'Last 7 Days') {
      // Get last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        dates.push(date);
      }
    } else if (period === 'Last 30 Days') {
      // Get last 30 days
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        dates.push(date);
      }
    } else {
      // For other periods, use the data as-is
      return null;
    }
    
    return dates;
  };
  
  // Prepare chart data for {t('performance.productionTrends')} (Line Chart)
  const getProductionTrendsChartData = () => {
    if (!production_trends || production_trends.length === 0) {
      return null;
    }
    
    // Create a map of existing data by date string (YYYY-MM-DD)
    const dataMap = new Map();
    production_trends.forEach(day => {
      const dateStr = day.date_saisie || day.date; // Prefer date_saisie for x-axis
      dataMap.set(dateStr, {
        badge_count: day.badge_count || 0,
        points: day.points || 0
      });
    });
    
    // Get date range for the period
    const dateRange = getDateRangeForPeriod(filters.period);
    
    let labels = [];
    let badgeData = [];
    let pointsData = [];
    
    if (dateRange && (filters.period === 'Today' || filters.period === 'Yesterday' || filters.period === 'Last 7 Days' || filters.period === 'Last 30 Days')) {
      // Fill in all dates in the range
      dateRange.forEach((date) => {
        // Format date to YYYY-MM-DD in local timezone (not UTC)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        const dayData = dataMap.get(dateStr) || { badge_count: 0, points: 0 };
        
        labels.push(date.toLocaleDateString(getDateLocale(), { month: 'short', day: 'numeric' }));
        badgeData.push(dayData.badge_count);
        pointsData.push(dayData.points);
      });
    } else {
      // For other periods, use data as-is
      labels = production_trends.map((day, index) => {
        // Format date label
        try {
          const date = new Date(day.date_saisie || day.date);
          if (isNaN(date.getTime())) {
            return day.date_saisie || day.date || t('performance.invalidDate');
          }
          return date.toLocaleDateString(getDateLocale(), { month: 'short', day: 'numeric' });
        } catch (e) {
          return day.date_saisie || day.date || t('error', { ns: 'common' });
        }
      });
      
      badgeData = production_trends.map(day => day.badge_count || 0);
      pointsData = production_trends.map(day => day.points || 0);
    }
    
    const lineA = isRsTenant ? RS_CHART_GLOBAL_LINES.a : '#1162d4';
    const lineB = isRsTenant ? RS_CHART_GLOBAL_LINES.b : '#60a5fa';
    return {
      labels,
      datasets: [
        {
          label: t('performance.badges'),
          data: badgeData,
          borderColor: lineA,
          backgroundColor: isRsTenant ? 'rgba(42, 42, 42, 0.08)' : 'rgba(17, 98, 212, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: lineA,
          pointBorderWidth: 2,
        },
        {
          label: t('performance.points'),
          data: pointsData,
          borderColor: lineB,
          backgroundColor: isRsTenant ? 'rgba(107, 107, 107, 0.08)' : 'rgba(96, 165, 250, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: lineB,
          pointBorderWidth: 2,
        },
      ],
    };
  };
  
  const productionTrendsChartData = getProductionTrendsChartData();
  
  // Line chart options for {t('performance.productionTrends')}
  const productionTrendsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
          color: '#617289',
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#111418',
        padding: 12,
        titleFont: {
          size: 12,
          weight: 'bold',
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#617289',
          font: {
            size: 11,
          },
          maxRotation: 45,
          minRotation: 0,
        },
        border: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f0f2f4',
        },
        ticks: {
          color: '#617289',
          font: {
            size: 11,
          },
          precision: 0,
        },
        border: {
          display: false,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };
  
  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col gap-2 max-w-2xl">
        <h1 className="text-[#111418] text-3xl md:text-4xl font-black leading-tight tracking-[-0.033em]">
          {t('performance.title')}
        </h1>
        <p className="text-[#617289] text-base font-normal leading-normal">
          {t('performance.subtitleDetail')}
        </p>
      </div>
      
      {/* {t('performance.filters')} */}
      <div className="bg-white p-4 rounded-xl border border-[#e5e7eb] shadow-sm">
        <div className="flex flex-col gap-2 mb-2">
          <div className="flex items-center gap-2 text-[#111418] font-semibold text-sm">
            <span className="material-symbols-outlined text-[20px] text-primary">filter_alt</span>
            {t('performance.filters')}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#617289]">{t('performance.timePeriod')}</span>
            <select
              className="form-select block w-full rounded-lg border-[#dbe0e6] bg-[#f9fafb] text-sm text-[#111418] focus:border-primary focus:ring-primary h-10"
              value={filters.period}
              onChange={(e) => handleFilterChange('period', e.target.value)}
            >
              <option value="Today">{t('performance.periods.today')}</option>
              <option value="Yesterday">{t('performance.periods.yesterday')}</option>
              <option value="Last 7 Days">{t('performance.periods.last7Days')}</option>
              <option value="Last 30 Days">{t('performance.periods.last30Days')}</option>
              <option value="This Quarter">{t('performance.periods.thisQuarter')}</option>
              <option value="YTD">{t('performance.periods.ytd')}</option>
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#617289]">{t('performance.center')}</span>
            <select
              className="form-select block w-full rounded-lg border-[#dbe0e6] bg-[#f9fafb] text-sm text-[#111418] focus:border-primary focus:ring-primary h-10"
              value={filters.centre}
              onChange={(e) => handleFilterChange('centre', e.target.value)}
            >
              <option value="">{t('performance.allCenters')}</option>
              {filter_options?.centres?.map((centre) => (
                <option key={centre.id} value={centre.id}>{centre.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#617289]">{t('performance.team')}</span>
            <select
              className="form-select block w-full rounded-lg border-[#dbe0e6] bg-[#f9fafb] text-sm text-[#111418] focus:border-primary focus:ring-primary h-10"
              value={filters.team}
              onChange={(e) => handleFilterChange('team', e.target.value)}
            >
              <option value="">{t('performance.allTeams')}</option>
              {(filter_options?.teams ?? []).map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </label>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* {t('performance.totalBadgesAwarded')} */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white p-5 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col justify-between h-32"
        >
          <div className="flex justify-between items-start">
            <p className="text-[#617289] text-sm font-medium">{t('performance.totalBadgesAwarded')}</p>
            <div className="bg-blue-50 p-1.5 rounded-md text-primary">
              <span className="material-symbols-outlined text-[20px]">emoji_events</span>
            </div>
          </div>
          <div>
            <p className="text-[#111418] text-3xl font-black tracking-tight">
              {summary?.total_badges_awarded?.toLocaleString() || 0}
            </p>
            {summary?.badge_trend && (
              <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${
                summary.badge_trend.change >= 0 ? 'text-emerald-600' : 'text-red-500'
              }`}>
                {/* <span className="material-symbols-outlined text-[16px]">
                  {summary.badge_trend.change >= 0 ? 'trending_up' : 'trending_down'}
                </span>
                <span>
                  {summary.badge_trend.change >= 0 ? '+' : ''}
                  {summary.badge_trend.change_percent}% vs previous period
                </span> */}
              </div>
            )}
          </div>
        </motion.div>
        
        {/* {t('performance.totalPointsDistributed')} */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white p-5 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col justify-between h-32"
        >
          <div className="flex justify-between items-start">
            <p className="text-[#617289] text-sm font-medium">{t('performance.totalPointsDistributed')}</p>
            <div className="bg-indigo-50 p-1.5 rounded-md text-indigo-600">
              <span className="material-symbols-outlined text-[20px]">stars</span>
            </div>
          </div>
          <div>
            <p className="text-[#111418] text-3xl font-black tracking-tight">
              {summary?.total_points_distributed?.toLocaleString() || 0}
            </p>
            {summary?.points_trend && (
              <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${
                summary.points_trend.change >= 0 ? 'text-emerald-600' : 'text-red-500'
              }`}>
                {/* <span className="material-symbols-outlined text-[16px]">
                  {summary.points_trend.change >= 0 ? 'trending_up' : 'trending_down'}
                </span>
                <span>
                  {summary.points_trend.change >= 0 ? '+' : ''}
                  {summary.points_trend.change_percent}% vs previous period
                </span> */}
              </div>
            )}
          </div>
        </motion.div>
        
        {/* Unique Employees */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white p-5 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col justify-between h-32"
        >
          <div className="flex justify-between items-start">
            <p className="text-[#617289] text-sm font-medium">{t('performance.employeesWithBadges')}</p>
            <div className="bg-emerald-50 p-1.5 rounded-md text-emerald-600">
              <span className="material-symbols-outlined text-[20px]">group</span>
            </div>
          </div>
          <div>
            <p className="text-[#111418] text-3xl font-black tracking-tight">
              {summary?.unique_employees_with_badges?.toLocaleString() || 0}
            </p>
          </div>
        </motion.div>
        
        {/* Average Points per Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-white p-5 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col justify-between h-32"
        >
          <div className="flex justify-between items-start">
            <p className="text-[#617289] text-sm font-medium">{t('performance.avgPointsPerBadge')}</p>
            <div className="bg-orange-50 p-1.5 rounded-md text-orange-600">
              <span className="material-symbols-outlined text-[20px]">calculate</span>
            </div>
          </div>
          <div>
            <p className="text-[#111418] text-3xl font-black tracking-tight">
              {summary?.total_badges_awarded > 0
                ? (summary.total_points_distributed / summary.total_badges_awarded).toFixed(1)
                : '0.0'}
            </p>
          </div>
        </motion.div>
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* {t('performance.badgeDistribution')} Chart */}
        <div className="bg-white p-6 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col">
          <h3 className="text-[#111418] text-lg font-bold mb-6">{t('performance.badgeDistribution')}</h3>
          <div className="flex items-center justify-center py-4 flex-1">
            {badgeChartData.segments.length > 0 ? (
              <div className="relative size-48 rounded-full" style={{
                background: `conic-gradient(${badgeChartData.segments.map((seg) => 
                  `${seg.color} ${seg.startPercent}% ${seg.endPercent}%`
                ).join(', ')})`
              }}>
                <div className="absolute inset-0 m-auto size-32 bg-white rounded-full flex items-center justify-center flex-col">
                  <span className="text-2xl font-bold text-[#111418]">
                    {badge_distribution?.length || 0}
                  </span>
                  <span className="text-xs text-[#617289]">{t('performance.badgeTypes')}</span>
                </div>
              </div>
            ) : (
              <div className="text-center text-[#617289]">{t('performance.noBadgeData')}</div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-2 mt-6 max-h-48 overflow-y-auto">
            {badge_distribution?.slice(0, 8).map((item, index) => {
              const segment = badgeChartData.segments[index];
              return (
                <div key={item.badge_id || index} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`size-3 rounded-full flex-shrink-0`} style={{ backgroundColor: segment?.color || '#e2e8f0' }}></div>
                    <span className="text-sm text-[#111418] font-medium truncate">{item.badge_name}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-sm text-[#617289]">{t('performance.badgesCount', { count: item.count })}</span>
                    <span className="text-sm font-semibold text-[#111418]">{t('performance.pts', { count: item.total_points })}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* {t('performance.productionTrends')} Chart */}
        <div className="bg-white p-6 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-[#111418] text-lg font-bold">{t('performance.productionTrends')}</h3>
          </div>
          <div className="flex-1 min-h-[300px]">
            {productionTrendsChartData ? (
              <Line data={productionTrendsChartData} options={productionTrendsChartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-[#617289] py-12">
                  <span className="material-symbols-outlined text-4xl mb-2 opacity-50 block">trending_up</span>
                  <p>{t('performance.noProductionTrendData')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* {t('performance.topPerformers')} Table */}
        <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-[#f0f2f4] flex justify-between items-center bg-white">
            <h3 className="text-[#111418] text-lg font-bold">{t('performance.topPerformers')}</h3>
          </div>
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-[#f9fafb] text-[#617289] font-medium">
                <tr>
                  <th className="px-6 py-3">{t('performance.employee')}</th>
                  <th className="px-6 py-3">{t('performance.team')}</th>
                  <th className="px-6 py-3 text-right">{t('performance.points')}</th>
                  <th className="px-6 py-3 text-right">{t('performance.badges')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f0f2f4]">
                {top_performers && top_performers.length > 0 ? (
                  top_performers.map((performer, index) => (
                    <tr key={performer.employee_id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-3.5">
                        <div className="flex flex-col">
                          <span className="font-medium text-[#111418]">{performer.name}</span>
                          <span className="text-xs text-[#617289]">{performer.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-3.5 text-[#617289]">{performer.team}</td>
                      <td className="px-6 py-3.5 text-right font-semibold text-[#111418]">
                        {performer.total_points.toLocaleString()}
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                          {performer.badge_count}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-3.5 text-center text-[#617289]">{t('performance.noPerformersData')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Badge by Team Table */}
        <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-[#f0f2f4] flex justify-between items-center bg-white">
            <h3 className="text-[#111418] text-lg font-bold">{t('performance.badgeDistributionByTeam')}</h3>
          </div>
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-[#f9fafb] text-[#617289] font-medium">
                <tr>
                  <th className="px-6 py-3">{t('performance.team')}</th>
                  <th className="px-6 py-3 text-right">{t('performance.badges')}</th>
                  <th className="px-6 py-3 text-right">{t('performance.totalPointsCol')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f0f2f4]">
                {badge_by_team && badge_by_team.length > 0 ? (
                  badge_by_team.map((team, index) => (
                    <tr key={team.team_id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-3.5 font-medium text-[#111418]">{team.team_name}</td>
                      <td className="px-6 py-3.5 text-right">{team.count.toLocaleString()}</td>
                      <td className="px-6 py-3.5 text-right font-semibold text-[#111418]">
                        {team.total_points.toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="px-6 py-3.5 text-center text-[#617289]">{t('performance.noTeamData')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalPerformanceTrackingPage;

